import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import useSeo from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Hook2Income AI — Turn One Topic Into a Mini-Course, Campaign & a Month of Content",
    description:
      "Turn any topic into a beginner-friendly mini-course, a full short-form content campaign and a month of scheduled social posts — guided by AI. No camera, voiceover or editing. Free to start.",
    path: "/",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-foreground p-12 text-background lg:flex">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="Hook2Income AI" className="h-10 w-10 object-contain" />
          <span className="font-head text-xl font-bold">
            Hook2Income<span style={{ color: "hsl(var(--gold))" }}> AI</span>
          </span>
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold leading-tight lg:text-5xl">
            Turn one topic into a mini-course and a month of content — with AI.
          </h1>
          <p className="mt-6 max-w-md text-base text-background/70">
            No camera. No voiceover. No editing. Just your idea, guided by AI into ready-to-publish content.
          </p>
        </div>
        <p className="text-sm text-background/50">Built for creators, coaches & affiliate marketers.</p>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <img src="/logo-mark.png" alt="Hook2Income AI" className="h-10 w-10 object-contain" />
            <span className="font-head text-xl font-bold">
              Hook2Income<span style={{ color: "hsl(var(--gold))" }}> AI</span>
            </span>
          </div>
          <h2 className="font-head text-3xl font-bold">Sign in</h2>
          <p className="mt-2 text-sm text-muted-foreground">Welcome back. Let's create something.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full rounded-full bg-primary py-6 text-base font-semibold hover:bg-primary/90"
            >
              {loading ? "Signing in..." : <>Sign in <ArrowRight size={18} className="ml-1" /></>}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            No account?{" "}
            <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="go-register">
              Create one
            </Link>
          </p>
          <p className="mt-3 text-center text-base text-muted-foreground">
            <Link to="/about" className="hover:underline" data-testid="login-about-link">About Hook2Income</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
